import React, { useEffect, useMemo, useState } from 'react';
import { useAngularService } from 'helpers/get_angular_service';
import Table from '../../components/table';
import api from '../../../helpers/api';
import sources from '../../../helpers/sources';
import Link from '../../components/link';
import notification from '../../../helpers/notification';
import dateFormatter from '../../../helpers/date_formatter';
import Input from '../../components/input';
import { useExtendState } from 'helpers/use_extend_state';
import limitString from 'helpers/limit_string';

interface MembershipPlan {
  id: number;
  label: string;
}

const MembershipPlanMembers = () => {
  const $rootScope = useAngularService('$rootScope');
  const $stateParams = useAngularService('$stateParams');
  const [filters, extendFilters] = useExtendState<{ contact_search: string }>({
    contact_search: '',
  });

  const [membershipPlan, setMembershipPlan] = useState<MembershipPlan>();
  const [loading, setLoading] = useState(false);
  const [memberships, setMemberships] = useState([]);

  const getMembershipPlan = () => {
    const url = sources.membershipPlan($rootScope.gym.id, $stateParams.membershipPlanId);
    api.get(url).then((data) => {
      setMembershipPlan(data);
    });
  };

  useEffect(() => {
    getMembershipPlan();
  }, []);

  const count = 25;

  const getMemberships = (page: number) => {
    setLoading(true);
    const url = `${sources.membershipPlan(
      $rootScope.gym.id,
      $stateParams.membershipPlanId,
    )}/memberships`;

    api.get(url, { page, count, ...filters }).then(
      (data) => {
        setLoading(false);
        setMemberships(data);
      },
      () => {
        setLoading(false);
        notification.error('Request could not be completed. Please try again');
      },
    );
  };

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        id: 'name',
        Cell: ({ row }) => (
          <span className="worx-table-primary-text">
            <Link
              route="gym.contacts.edit"
              routeParams={{ contactType: 'members', contactId: row.original.contact_id }}
            >
              {row.original.contact_name}
            </Link>
          </span>
        ),
      },

      {
        Header: 'Start date',
        id: 'start_date',
        Cell: ({ row }) => <span>{dateFormatter(row.original.start_date, 'date')}</span>,
      },
      {
        Header: 'Added',
        id: 'added',
        Cell: ({ row }) => <span>{dateFormatter(row.original.created_at, 'mediumDate')}</span>,
      },
      {
        Header: 'Added from',
        id: 'added_from',
        Cell: ({ row }) => <span>{row.original.humanized_source}</span>,
      },
      {
        Header: 'Expiration date',
        id: 'expiration_date',
        Cell: ({ row }) => <span>{dateFormatter(row.original.expiration_date, 'date')}</span>,
      },
    ],
    [],
  );

  if (!membershipPlan) return null;

  const title = `Members with ${limitString(membershipPlan.label, 40)} memberships`;

  return (
    <section data-autoscroll="false" className="content-section manage-memberships">
      <header className="page-header">
        <h1 className="page-header__title">{title}</h1>
        <div className="page-header__buttons">
          <Link type="button" variant="hollow" route="gym.membership-plans.list">
            Back
          </Link>
        </div>
      </header>

      <div className="container">
        <main>
          <div className="table-header">
            <Input
              value={filters.contact_search}
              onChange={(contact_search) => extendFilters({ contact_search })}
              placeholder="Search by contact name"
            />
          </div>

          <Table
            columns={columns}
            data={memberships}
            emptyText="No Memberships found"
            loading={loading}
            pageSize={count}
            filters={filters}
            loadData={(page) => getMemberships(page)}
            variant="v2"
          />
        </main>
      </div>
    </section>
  );
};

angular.module('gym').value('MembershipPlanMembers', MembershipPlanMembers);
